if (!customElements.get('ciari-sticky-atc')) {
  customElements.define('ciari-sticky-atc', class CiariStickyAtc extends HTMLElement {
    connectedCallback() {
      const { sectionId } = this.dataset;

      // The PDP wrapper keeps the theme's filled fadeIn animation, which makes it a
      // stacking context: inside it, z-index cannot lift a fixed bar over later
      // sections. Re-parenting to <body> also runs this callback again.
      if (this.parentElement !== document.body) {
        document.querySelectorAll(`body > ciari-sticky-atc[data-section-id="${sectionId}"]`).forEach((bar) => bar.remove());
        document.body.append(this);
        return;
      }

      const productInformation = document.querySelector(`product-information[data-section-id="${sectionId}"]`);
      // Observe <product-form>, not the button: the theme swaps the button out with
      // replaceWith() on every variant change, which would detach the observed target.
      this.productForm = productInformation?.querySelector('product-form');
      this.source = productInformation?.querySelector('template[data-sticky-atc-source]');
      if (!this.productForm || !this.source) return;

      this.ready = true;
      this.visible = false;
      this.render();
      document.addEventListener('shopify:section:unload', this.onSectionUnload);
      this.addEventListener('click', this.onClick);
      window.eventBus?.on('variant:updated', this.onVariantUpdated);
      window.eventBus?.on('cart:added', this.onCartAdded);

      // Observers only fire on crossings, so a jump from below the viewport straight
      // to above it (end key, anchor, restored scroll) is invisible to a single one.
      // "In view" plus "in view or above" changes on every such jump.
      this.inView = false;
      this.reached = false;
      const update = () => this.toggle(this.reached && !this.inView);

      this.viewObserver = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        update();
      });
      this.reachedObserver = new IntersectionObserver(([entry]) => {
        this.reached = entry.isIntersecting;
        update();
      }, { rootMargin: '100000px 0px 0px 0px' });

      this.viewObserver.observe(this.productForm);
      this.reachedObserver.observe(this.productForm);

      this.resizeObserver = new ResizeObserver(() => this.publishHeight());
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      if (!this.ready) return;
      this.ready = false;

      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      this.removeEventListener('click', this.onClick);
      window.eventBus?.off('variant:updated', this.onVariantUpdated);
      window.eventBus?.off('cart:added', this.onCartAdded);
      clearTimeout(this.addedTimer);
      this.viewObserver?.disconnect();
      this.reachedObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.toggle(false);
    }

    render() {
      this.querySelector('.ciari-sticky-atc__inner')?.replaceChildren(this.source.content.cloneNode(true));
    }

    onSectionUnload = ({ detail }) => {
      if (detail?.sectionId === this.dataset.sectionId) this.remove();
    };

    get mainButton() {
      return this.productForm.querySelector('[name="add"]');
    }

    onClick = (event) => {
      if (!event.target.closest('[data-sticky-atc-button]')) return;

      const { mainButton } = this;
      if (!mainButton || mainButton.disabled) return;

      // Required line-item fields live next to the main button; send the shopper
      // there instead of submitting a form whose errors they cannot see.
      const { form } = mainButton;
      if (form && !form.checkValidity()) {
        mainButton.scrollIntoView({ block: 'center', behavior: 'smooth' });
        form.reportValidity();
        return;
      }

      this.setBusy(true);
      mainButton.click();
    };

    // The theme's "show added message" cart action only writes to the main button,
    // which is off screen whenever this bar is visible; mirror it here.
    onCartAdded = ({ sectionId } = {}) => {
      if (sectionId && sectionId !== this.dataset.sectionId) return;

      this.setBusy(false);
      const addedText = this.mainButton?.querySelector('[data-cart-added-text]');
      const label = this.querySelector('[data-sticky-atc-label]');
      if (!addedText || addedText.style.display !== 'block' || !label) {
        this.syncButton();
        return;
      }

      label.textContent = addedText.textContent.trim();
      clearTimeout(this.addedTimer);
      this.addedTimer = setTimeout(() => this.syncButton(), 2000);
    };

    setBusy(busy) {
      const button = this.querySelector('[data-sticky-atc-button]');
      if (!button) return;
      button.toggleAttribute('aria-busy', busy);
      button.disabled = busy || Boolean(this.mainButton?.disabled);
    }

    onVariantUpdated = ({ sectionId } = {}) => {
      if (sectionId && sectionId !== this.dataset.sectionId) return;
      this.render();
      this.syncButton();
    };

    syncButton() {
      const { mainButton } = this;
      const button = this.querySelector('[data-sticky-atc-button]');
      const label = this.querySelector('[data-sticky-atc-label]');
      if (!mainButton || !button || !label) return;

      button.disabled = mainButton.disabled;
      label.textContent = mainButton.querySelector('[data-add-to-cart-text]')?.textContent.trim() || label.textContent;
    }

    publishHeight() {
      document.documentElement.style.setProperty('--ciari-sticky-atc-height', `${this.offsetHeight}px`);
    }

    toggle(visible) {
      if (visible === this.visible) return;
      this.visible = visible;

      if (visible) {
        this.syncButton();
        this.publishHeight();
      }

      this.classList.toggle('is-visible', visible);
      this.toggleAttribute('inert', !visible);
      this.setAttribute('aria-hidden', String(!visible));
      document.documentElement.classList.toggle('ciari-sticky-atc-visible', visible);
      this.liftChat(visible);
    }

    // Shopify Inbox positions its launcher from --shopify-chat-activator-offset, which
    // the app sets on :root. Adding the bar height on the host keeps the launcher above
    // the bar without reaching into the app's shadow DOM.
    liftChat(visible) {
      const chat = document.querySelector('shopify-chat');
      if (!chat) return;

      if (!visible) {
        chat.style.removeProperty('--shopify-chat-activator-offset');
        return;
      }

      const offset = getComputedStyle(document.documentElement).getPropertyValue('--shopify-chat-activator-offset').trim() || '0px';
      chat.style.setProperty('--shopify-chat-activator-offset', `calc(${offset} + var(--ciari-sticky-atc-height, 0px))`);
    }
  });
}
